import { modals } from '@mantine/modals'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'
import {
  showErrorNotification,
  showSuccessNotification,
} from '@v2/domains/notification/notification.hook'

import { useAppSelector } from 'src/v2/store/hooks'

import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
} from '../api/user.api'
import { selectCurrentOrganization } from '../selectors/user.selectors'

export const useUserMutations = () => {
  const { id: organizationId } = useAppSelector(selectCurrentOrganization)

  const [createUserMutation, createUserStatus] = useCreateUserMutation()
  const [updateUserMutation, updateUserStatus] = useUpdateUserMutation()
  const [deleteUserMutation, deleteUserStatus] = useDeleteUserMutation()

  const createUser = async (
    userData: Omit<any, 'id'>,
  ): Promise<string | null> => {
    try {
      const createUserData: any = {
        ...userData,
        organizationId,
      }

      const response = await createUserMutation(createUserData).unwrap()
      showSuccessNotification('User successfully created!')
      return response.id || null
    } catch (err: any) {
      handleMutationError(err, 'Error updating user')
      return null
    }
  }

  const updateUser = async (userData: any): Promise<boolean> => {
    try {
      if (!userData.id) {
        const msg = 'User ID is required for update'
        showErrorNotification(msg)
        return false
      }

      await updateUserMutation(userData).unwrap()
      showSuccessNotification('User succesfuly updated!')
      return true
    } catch (err: any) {
      handleMutationError(err, 'Error updating user')
      return false
    }
  }

  const deleteUser = async (userId: string): Promise<boolean> => {
    try {
      await deleteUserMutation(userId).unwrap()
      showSuccessNotification('User succesfuly deleted!')
      modals.closeAll()
      return true
    } catch (err: any) {
      handleMutationError(err, 'Error updating user')
      return false
    }
  }

  return {
    createUser,
    updateUser,
    deleteUser,
    isCreating: createUserStatus.isLoading,
    isUpdating: updateUserStatus.isLoading,
    isDeleting: deleteUserStatus.isLoading,
    isCreateSuccess: createUserStatus.isSuccess,
    isUpdateSuccess: updateUserStatus.isSuccess,
    isDeleteSuccess: deleteUserStatus.isSuccess,
  }
}
